import {
  Flex,
  Avatar,
  useColorModeValue,
  Button,
  MenuButton,
  Menu,
  MenuList,
  Divider,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import {
  GlobalNavButtonItem,
  GlobalNavRouterLinkItemWithUpsell,
} from './GlobalNav';
import { Typography } from 'packages/common-frontend/components/Typography';
import { logout, useUserAuthUnsafe } from '~/contexts/UserAuthProvider';
import { permissions as settingsPermissions } from '~/core/routes/settings/Router';
import { Icon } from '~/packages/common-frontend/components/icons';

// Exports
type UserMenuProps = { isAdmin?: boolean };
export const UserMenu = ({ isAdmin = false }: UserMenuProps) => {
  const userAuth = useUserAuthUnsafe();
  const menuControls = useDisclosure();
  const orgTypeLabel = useMemo(() => {
    if (userAuth.status === 'logged-in') {
      if (userAuth.user.userType === 'BORROWER') return 'Borrower';
      if (userAuth.user.userType === 'CAPITAL_PROVIDER') return 'Lender';
      return 'Admin';
    }
    return null;
  }, [userAuth.status, userAuth.user?.userType]);

  const background = useColorModeValue('gray.100', 'gray.900');
  const color = useColorModeValue('gray.800', 'gray.100');

  if (userAuth.status !== 'logged-in') return null;

  return (
    <Flex flexDirection="row" gap="0.5rem" alignItems="center">
      <Menu placement="bottom-end" {...menuControls}>
        <MenuButton
          as={Button}
          variant="v2-ghost"
          colorScheme="gray"
          height="fit-content"
          padding="0.5rem"
          paddingRight="1rem"
          gap="0.5rem"
          alignItems="baseline"
          rightIcon={<Icon icon="chevron-down" />}
        >
          <Flex gap="1rem">
            <Avatar
              height="3rem"
              width="3rem"
              background={background}
              color={color}
              icon={<Icon icon="user" size="1.5rem" />}
            />
            <Flex
              direction="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Typography variant="base">
                {userAuth.user.firstName} {userAuth.user.lastName}
              </Typography>
              <Typography variant="detail">
                {userAuth.user.organization.name}
                {orgTypeLabel ? ` - ${orgTypeLabel}` : ''}
              </Typography>
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList
          padding="0.5rem"
          gap="0.25rem"
          display="flex"
          flexDirection="column"
        >
          {!isAdmin && (
            <>
              <MenuItem
                as={GlobalNavRouterLinkItemWithUpsell}
                permissions={settingsPermissions}
                GlobalNavRouterLinkItemProps={{
                  to: '/settings',
                  label: 'Settings',
                  icon: <Icon icon="gear" />,
                  colorScheme: 'gray',
                  forceFullSize: true,
                  onClick: () => menuControls.onClose(),
                }}
              />
              <Divider margin="0.25rem 0" width="auto" />
            </>
          )}
          <GlobalNavButtonItem
            icon={<Icon icon="arrow-right-from-bracket" />}
            colorScheme="gray"
            label="Log Out"
            forceFullSize
            onClick={() => logout()}
          />
        </MenuList>
      </Menu>
    </Flex>
  );
};
